import React, { useEffect, useMemo, useState } from "react";
import {
  CustomerRequest,
  CustomerDomain,
  ProjectIntake,
  ProjectIntakeMessage,
  ProjectMilestone,
  ProductOrder,
  Profile,
  isSupabaseConfigured,
  supabase,
} from "../lib/supabase";
import { formatRand, getProductPlan, intakeStarterQuestions } from "../lib/productPlans";

type Mode = 'login' | 'signup';

const statusLabel = (value: string) =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const ClientPage = () => {
  const params = new URLSearchParams(window.location.search);
  const initialMode = params.get('mode') === 'signup' ? 'signup' : 'login';

  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [customerDomains, setCustomerDomains] = useState<CustomerDomain[]>([]);
  const [intakes, setIntakes] = useState<ProjectIntake[]>([]);
  const [messages, setMessages] = useState<ProjectIntakeMessage[]>([]);
  const [milestones, setMilestones] = useState<ProjectMilestone[]>([]);
  const [requests, setRequests] = useState<CustomerRequest[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChatSubmitting, setIsChatSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [requestInput, setRequestInput] = useState('');
  const [error, setError] = useState('');
  const [portalError, setPortalError] = useState('');

  const selectedOrder = useMemo(
    () => orders.find((order) => order.id === selectedOrderId) || orders[0],
    [orders, selectedOrderId],
  );

  const selectedIntake = useMemo(
    () => intakes.find((intake) => intake.order_id === selectedOrder?.id),
    [intakes, selectedOrder?.id],
  );
  const selectedCustomerDomain = useMemo(
    () => customerDomains.find((domain) => domain.order_id === selectedOrder?.id),
    [customerDomains, selectedOrder?.id],
  );

  const selectedMilestones = milestones.filter((milestone) => milestone.order_id === selectedOrder?.id);
  const selectedRequests = requests.filter((request) => request.order_id === selectedOrder?.id);
  const selectedDesiredDomain =
    selectedCustomerDomain?.full_domain ||
    selectedOrder?.desired_domain ||
    selectedIntake?.desired_domain ||
    (selectedOrder?.metadata?.spec as any)?.domain?.desiredDomain ||
    '';
  const selectedNeedsDomain = Boolean(
    selectedOrder?.domain_needs_registration ||
      selectedIntake?.domain_needs_registration ||
      (selectedOrder?.metadata?.spec as any)?.domain?.needsRegistration,
  );
  const selectedDomainPurchased = Boolean(
    selectedCustomerDomain?.status === 'registered' || selectedOrder?.domain_purchased || selectedIntake?.domain_purchased,
  );

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setError('Client login is temporarily unavailable.');
      setIsLoading(false);
      return;
    }

    const loadSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) await fetchProfile(session.user.id);
      setIsLoading(false);
    };

    loadSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        await fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setOrders([]);
        setCustomerDomains([]);
        setIntakes([]);
        setMessages([]);
        setMilestones([]);
        setRequests([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (profile) {
      loadPortalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (selectedIntake) {
      loadMessages(selectedIntake.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIntake?.id]);

  const fetchProfile = async (userId: string) => {
    const { data, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError) throw profileError;
    setProfile(data);
  };

  const loadPortalData = async () => {
    setIsPortalLoading(true);
    setPortalError('');

    try {
      const { data: orderRows, error: ordersError } = await supabase
        .from('product_orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      const nextOrders = (orderRows || []) as ProductOrder[];
      setOrders(nextOrders);
      if (!selectedOrderId && nextOrders[0]) setSelectedOrderId(nextOrders[0].id);

      if (nextOrders.length === 0) {
        setCustomerDomains([]);
        setIntakes([]);
        setMilestones([]);
        setRequests([]);
        return;
      }

      const orderIds = nextOrders.map((order) => order.id);

      const [domainResult, intakeResult, milestoneResult, requestResult] = await Promise.all([
        supabase.from('customer_domains').select('*').in('order_id', orderIds),
        supabase.from('project_intakes').select('*').in('order_id', orderIds),
        supabase.from('project_milestones').select('*').in('order_id', orderIds).order('position', { ascending: true }),
        supabase.from('customer_requests').select('*').in('order_id', orderIds).order('created_at', { ascending: false }),
      ]);

      if (domainResult.error) throw domainResult.error;
      if (intakeResult.error) throw intakeResult.error;
      if (milestoneResult.error) throw milestoneResult.error;
      if (requestResult.error) throw requestResult.error;

      setCustomerDomains((domainResult.data || []) as CustomerDomain[]);
      setIntakes((intakeResult.data || []) as ProjectIntake[]);
      setMilestones((milestoneResult.data || []) as ProjectMilestone[]);
      setRequests((requestResult.data || []) as CustomerRequest[]);
    } catch (portalLoadError: any) {
      setPortalError(
        portalLoadError.message ||
          'Portal data could not be loaded. Apply the product order Supabase migration if this is a new database.',
      );
    } finally {
      setIsPortalLoading(false);
    }
  };

  const loadMessages = async (intakeId: string) => {
    const { data, error: messagesError } = await supabase
      .from('project_intake_messages')
      .select('*')
      .eq('intake_id', intakeId)
      .order('created_at', { ascending: true });

    if (!messagesError) setMessages((data || []) as ProjectIntakeMessage[]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    setError('');

    try {
      if (mode === 'signup') {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/client`,
            data: { full_name: fullName },
          },
        });

        if (signUpError) throw signUpError;
        setMessage('Account created. Please check your email and click the verification link before logging in.');
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        setMessage('Logged in successfully.');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  const sendIntakeMessage = async (content?: string) => {
    const body = (content || chatInput).trim();
    if (!body || !selectedIntake) return;

    setIsChatSubmitting(true);
    setChatInput('');

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) throw new Error('Please log in again.');

      const response = await fetch('/api/intake-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ intakeId: selectedIntake.id, message: body }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'AI intake failed.');

      setIntakes((current) => current.map((intake) => (intake.id === result.intake.id ? result.intake : intake)));
      await loadMessages(selectedIntake.id);
    } catch (chatError: any) {
      setPortalError(chatError.message || 'AI intake failed.');
    } finally {
      setIsChatSubmitting(false);
    }
  };

  const submitRequest = async () => {
    if (!requestInput.trim() || !selectedOrder) return;

    const { error: requestError } = await supabase.from('customer_requests').insert({
      order_id: selectedOrder.id,
      user_id: selectedOrder.user_id,
      message: requestInput.trim(),
      status: 'new',
    });

    if (requestError) {
      setPortalError(requestError.message);
      return;
    }

    setRequestInput('');
    await loadPortalData();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div style={{ color: 'var(--text-primary)' }}>Loading...</div>
      </div>
    );
  }

  if (profile) {
    const plan = selectedOrder ? getProductPlan(selectedOrder.tier) : null;

    return (
      <div
        className="min-h-screen w-full px-4 pt-32 pb-16 text-orange-50"
        style={{
          background:
            'radial-gradient(circle at 78% 8%, rgba(249,115,22,0.22), transparent 34%), linear-gradient(135deg, #050505 0%, #0b0b0d 48%, #17100b 100%)',
          ['--text-primary' as string]: '#fff7ed',
          ['--text-secondary' as string]: '#fed7aa',
          ['--accent-primary' as string]: '#f97316',
          ['--border-primary' as string]: 'rgba(251,146,60,0.28)',
          ['--card-bg' as string]: 'rgba(10,10,12,0.72)',
          ['--bg-secondary' as string]: 'rgba(255,255,255,0.045)',
        }}
      >
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 flex flex-col gap-4 rounded-[2rem] border border-orange-300/20 bg-black/35 p-6 shadow-[0_0_52px_rgba(249,115,22,0.12)] backdrop-blur-xl md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.24em] mb-3" style={{ color: 'var(--accent-primary)' }}>
                {profile.role} portal
              </p>
              <h1 className="text-3xl md:text-5xl font-black mb-3 tracking-tight" style={{ color: 'var(--text-primary)' }}>
                Welcome, {profile.full_name || profile.email}
              </h1>
              <p className="max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
                Track orders, shape the AI-assisted project brief, review the checklist, and send requests from one place.
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="rounded-full border border-orange-300/35 bg-orange-500/10 px-5 py-2 text-sm font-black uppercase tracking-[0.14em] text-orange-100 transition-colors hover:border-orange-200/70"
            >
              Logout
            </button>
          </div>

          {portalError && <div className="mb-5 rounded-2xl border border-red-400/40 bg-red-500/10 p-4 text-red-100">{portalError}</div>}

          {orders.length === 0 ? (
            <div className="rounded-[2rem] border p-8 backdrop-blur-xl" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
              <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>No website orders yet</h2>
              <p className="mb-5" style={{ color: 'var(--text-secondary)' }}>
                Choose a package to create your first order and begin the AI intake.
              </p>
              <a
                href="/product"
                className="inline-flex rounded-md px-5 py-2 font-medium"
                style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
              >
                View packages
              </a>
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
              <aside className="space-y-4">
                {orders.map((order) => {
                  const orderPlan = getProductPlan(order.tier);
                  return (
                    <button
                      key={order.id}
                      type="button"
                      onClick={() => setSelectedOrderId(order.id)}
                    className="w-full rounded-2xl border p-4 text-left transition-colors"
                      style={{
                        backgroundColor: selectedOrder?.id === order.id ? 'rgba(249,115,22,0.16)' : 'var(--card-bg)',
                        borderColor: 'var(--border-primary)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <p className="text-sm uppercase tracking-wider opacity-80">{orderPlan?.name || order.tier}</p>
                      <p className="mt-2 text-xl font-bold">{statusLabel(order.status)}</p>
                      <p className="mt-1 text-sm opacity-80">
                        {formatRand(order.total_amount || 0)} {order.billing_mode === 'monthly' ? 'monthly' : 'once-off'}
                      </p>
                    </button>
                  );
                })}
              </aside>

              <section className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl border p-5 md:col-span-2" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
                    <p className="text-sm uppercase tracking-widest" style={{ color: 'var(--accent-primary)' }}>Selected build</p>
                    <h2 className="mt-2 text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{plan?.name || selectedOrder?.tier}</h2>
                    <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>{plan?.shortDescription}</p>
                  </div>
                  <div className="rounded-2xl border p-5" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
                    <p className="text-sm uppercase tracking-widest" style={{ color: 'var(--accent-primary)' }}>Status</p>
                    <p className="mt-2 text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{selectedOrder && statusLabel(selectedOrder.status)}</p>
                    {isPortalLoading && <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>Refreshing...</p>}
                  </div>
                </div>

                {(selectedNeedsDomain || selectedDesiredDomain) && (
                  <div className="rounded-2xl border p-5" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-sm uppercase tracking-widest" style={{ color: 'var(--accent-primary)' }}>Domain</p>
                        <h3 className="mt-2 text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                          {selectedDesiredDomain || 'Domain registration requested'}
                        </h3>
                        <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {selectedDomainPurchased
                            ? 'Your domain has been registered and secured by the team.'
                            : selectedCustomerDomain?.purchase_paid
                              ? 'Payment is confirmed. Your domain is queued for registration and DNS setup.'
                              : selectedCustomerDomain?.availability_status === 'available'
                                ? 'This domain checked as available. Checkout payment must clear before registration starts.'
                                : 'Your domain is being handled. We will confirm availability, registration, DNS, and launch connection.'}
                        </p>
                        {selectedCustomerDomain ? (
                          <p className="mt-2 text-xs uppercase tracking-[0.14em]" style={{ color: 'var(--accent-primary)' }}>
                            {selectedCustomerDomain.status.replace(/_/g, ' ')}
                            {selectedCustomerDomain.purchase_amount ? ` · ${formatRand(selectedCustomerDomain.purchase_amount)}` : ''}
                          </p>
                        ) : null}
                      </div>
                      <div className="inline-flex items-center gap-3 rounded-full border px-4 py-2" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}>
                        {!selectedDomainPurchased && (
                          <span className="h-4 w-4 animate-spin rounded-full border border-orange-300/30 border-t-orange-300" />
                        )}
                        <span className="text-xs font-black uppercase tracking-[0.16em]">
                          {selectedDomainPurchased
                            ? 'Registered'
                            : selectedCustomerDomain?.purchase_paid
                              ? 'Paid'
                              : selectedCustomerDomain?.availability_status === 'available'
                                ? 'Available'
                                : 'In progress'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="rounded-2xl border p-5" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
                  <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Project milestones</h3>
                  <div className="grid gap-3 md:grid-cols-5">
                    {selectedMilestones.map((milestone) => (
                      <div key={milestone.id} className="rounded-xl border p-3" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}>
                        <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{milestone.title}</p>
                        <p className="mt-1 text-xs uppercase tracking-wider" style={{ color: 'var(--accent-primary)' }}>{statusLabel(milestone.status)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                  <div className="rounded-2xl border p-5" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
                    <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>AI intake</h3>
                    <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Answer the guided questions. The assistant turns the conversation into the project checklist.
                    </p>

                    {messages.length === 0 && (
                      <div className="my-4 flex flex-wrap gap-2">
                        {intakeStarterQuestions.map((question) => (
                          <button
                            key={question}
                            type="button"
                            onClick={() => sendIntakeMessage(question)}
                            className="rounded-full border px-3 py-2 text-xs"
                            style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 max-h-80 space-y-3 overflow-y-auto pr-1">
                      {messages.map((item) => (
                        <div
                          key={item.id}
                          className={`rounded-2xl p-3 ${item.role === 'client' ? 'ml-8' : 'mr-8'}`}
                          style={{
                            backgroundColor: item.role === 'client' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                            color: item.role === 'client' ? '#fff' : 'var(--text-primary)',
                          }}
                        >
                          <p className="whitespace-pre-wrap text-sm">{item.content}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 flex gap-2">
                      <input
                        value={chatInput}
                        onChange={(event) => setChatInput(event.target.value)}
                        placeholder="Tell the AI what this website needs..."
                        className="min-w-0 flex-1 rounded-md border px-3 py-2"
                        style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                      />
                      <button
                        type="button"
                        onClick={() => sendIntakeMessage()}
                        disabled={isChatSubmitting || !selectedIntake}
                        className="rounded-md px-4 py-2 font-medium disabled:opacity-50"
                        style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
                      >
                        {isChatSubmitting ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="rounded-2xl border p-5" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
                      <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Spec checklist</h3>
                      {selectedIntake?.spec_checklist?.length ? (
                        <ul className="space-y-2">
                          {selectedIntake.spec_checklist.map((item) => (
                            <li key={item} className="flex gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                              <span style={{ color: 'var(--accent-primary)' }}>✓</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p style={{ color: 'var(--text-secondary)' }}>Start the intake chat to generate the first checklist.</p>
                      )}
                    </div>

                    <div className="rounded-2xl border p-5" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
                      <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Requests</h3>
                      <textarea
                        value={requestInput}
                        onChange={(event) => setRequestInput(event.target.value)}
                        placeholder="Send a request or note to the team..."
                        rows={4}
                        className="w-full rounded-md border px-3 py-2"
                        style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                      />
                      <button
                        type="button"
                        onClick={submitRequest}
                        className="mt-3 rounded-md px-4 py-2 font-medium"
                        style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
                      >
                        Send request
                      </button>
                      <div className="mt-4 space-y-2">
                        {selectedRequests.map((request) => (
                          <div key={request.id} className="rounded-xl border p-3 text-sm" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}>
                            {request.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 pt-32 pb-16 text-orange-50"
      style={{
        background:
          'radial-gradient(circle at 78% 8%, rgba(249,115,22,0.22), transparent 34%), linear-gradient(135deg, #050505 0%, #0b0b0d 48%, #17100b 100%)',
      }}
    >
      <div className="w-full max-w-md rounded-[2rem] border border-orange-300/25 bg-[#060505] p-8 shadow-[0_0_70px_rgba(249,115,22,0.2)]">
        <h1 className="text-3xl font-black mb-2 tracking-tight text-orange-50">
          {mode === 'signup' ? 'Create client account' : 'Client login'}
        </h1>
        <p className="mb-6 text-orange-100/65">
          {mode === 'signup'
            ? 'Create your DesignDotDev account to order a website and begin the AI-assisted project intake.'
            : 'Sign in to view orders, project specs, milestones, and client requests.'}
        </p>

        {message && <div className="mb-4 rounded-2xl border border-orange-300/25 bg-orange-500/10 p-3 text-orange-100">{message}</div>}
        {error && <div className="mb-4 rounded-2xl border border-red-400/40 bg-red-500/10 p-3 text-red-100">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
                style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
            </div>
          )}

          <div>
            <label htmlFor="clientEmail" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
              Email
            </label>
            <input
              id="clientEmail"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
              style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
            />
          </div>

          <div>
            <label htmlFor="clientPassword" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
              Password
            </label>
            <input
              id="clientPassword"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
              style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-2 font-medium rounded-md hover:opacity-90 transition disabled:opacity-50"
            style={{ background: 'linear-gradient(to right, var(--accent-primary), var(--accent-secondary))', color: '#fff' }}
          >
            {isSubmitting ? 'Working...' : mode === 'signup' ? 'Create account' : 'Login'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => {
            setMode(mode === 'signup' ? 'login' : 'signup');
            setMessage('');
            setError('');
          }}
          className="mt-5 text-sm underline"
          style={{ color: 'var(--accent-primary)' }}
        >
          {mode === 'signup' ? 'Already have an account? Login' : 'Need an account? Sign up'}
        </button>
      </div>
    </div>
  );
};

export default ClientPage;
